// src/pages/login/LoginPage.jsx
// ✅ UPDATED VERSION - Now properly saves permissions using saveAuthData()

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  User, 
  Lock, 
  Eye, 
  EyeOff, 
  AlertCircle,
  Radio,
  Building2,
  Network,
  Signal,
  Wifi
} from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';

// ✅ CRITICAL IMPORT - Use the saveAuthData function
import { saveAuthData, isAuthenticated, debugAuthState } from '../../components/auth/authUtils';

const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api';

const LoginPage = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Check if already authenticated
  useEffect(() => {
    if (isAuthenticated()) {
      navigate('/dashboard', { replace: true });
    }
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      console.log('🔐 Attempting login for:', username);
      
      // ✅ Call backend login API
      const response = await axios.post(`${BASE_URL}/auth/login`, {
        username: username,
        password: password
      });

      console.log('📦 Login response received:', response.data);

      const loginData = response.data;

      // ✅ CRITICAL FIX: Use saveAuthData() instead of manual localStorage.setItem()
      const saveSuccess = saveAuthData(loginData, rememberMe);

      if (!saveSuccess) {
        throw new Error('Failed to save authentication data');
      }

      // Debug: Log auth state after save
      debugAuthState();

      // Success toast
      const displayName = loginData.user?.fullName || loginData.user?.username || 'User';
      toast.success(`Selamat datang, ${displayName}!`, {
        icon: '👋',
        duration: 2000,
      });

      // Redirect to dashboard
      console.log('✅ Login successful, redirecting to dashboard');
      navigate('/dashboard', { replace: true });
      
    } catch (error) {
      console.error('❌ Login error:', error);
      
      // Handle different error cases
      if (error.response?.status === 401) {
        setError('Username atau password salah');
      } else if (error.response?.status === 403) {
        setError('Akun Anda tidak aktif. Hubungi administrator.');
      } else if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else if (error.message) {
        setError(error.message);
      } else {
        setError('Login gagal. Silakan coba lagi.');
      }
      
      toast.error('Login gagal!', { duration: 3000 });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Add keyframes in style tag */}
      <style jsx>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }
        
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        
        .animate-float-delayed-1 {
          animation: float 6s ease-in-out infinite;
          animation-delay: 1s;
        }
        
        .animate-float-delayed-2 {
          animation: float 6s ease-in-out infinite;
          animation-delay: 2s;
        }
        
        .animate-pulse-delayed-1 {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
          animation-delay: 1.5s;
        }
      `}</style>

      {/* Left Section - Branding with CSS Background */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden">
        {/* Animated Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900">
          {/* Animated circles */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-red-500 opacity-20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-blue-500 opacity-20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
          <div className="absolute top-1/3 left-1/3 w-64 h-64 bg-cyan-400 opacity-10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
        </div>

        {/* Network Pattern Overlay */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute bottom-20 right-20 animate-float-delayed-1">
            <Radio className="w-48 h-48 text-white" />
          </div>
          
          <div className="absolute top-1/4 right-1/4 animate-pulse">
            <Wifi className="w-16 h-16 text-white" />
          </div>
        </div>

        {/* Content */}
        <div className="relative z-10 p-12 flex flex-col justify-between w-full text-white">
          {/* Top Section */}
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white bg-opacity-20 backdrop-blur-sm rounded-full mb-8">
              <Signal className="w-4 h-4 animate-pulse" />
              <span className="text-sm font-semibold">Telecommunications Infrastructure</span>
            </div>

            <h1 className="text-5xl font-bold mb-6 leading-tight">
              Building Indonesia's<br />
              Digital Infrastructure
            </h1>
            <p className="text-xl text-blue-100 max-w-lg mb-8">
              Professional Project Management System untuk Tower Site & Fiber Optic Implementation
            </p>

            {/* Features */}
            <div className="space-y-4 max-w-lg">
              <div className="flex items-start gap-4 p-4 bg-white bg-opacity-10 backdrop-blur-sm rounded-lg border border-white border-opacity-20 hover:bg-opacity-20 transition-all">
                <div className="w-12 h-12 bg-red-500 rounded-lg flex items-center justify-center flex-shrink-0 shadow-lg">
                  <Building2 className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-bold text-lg mb-1">Tower Site Projects</h3>
                  <p className="text-blue-100 text-sm">Comprehensive tower construction and installation management</p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-white bg-opacity-10 backdrop-blur-sm rounded-lg border border-white border-opacity-20 hover:bg-opacity-20 transition-all">
                <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center flex-shrink-0 shadow-lg">
                  <Radio className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-bold text-lg mb-1">Fiber Optic Networks</h3>
                  <p className="text-blue-100 text-sm">End-to-end fiber optic deployment and segment tracking</p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-white bg-opacity-10 backdrop-blur-sm rounded-lg border border-white border-opacity-20 hover:bg-opacity-20 transition-all">
                <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center flex-shrink-0 shadow-lg">
                  <Network className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-bold text-lg mb-1">Integrated PMO</h3>
                  <p className="text-blue-100 text-sm">Real-time monitoring, budgeting, and progress tracking</p>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Stats */}
          <div className="grid grid-cols-3 gap-6 mt-12">
            <div className="text-center p-4 bg-white bg-opacity-10 backdrop-blur-sm rounded-lg border border-white border-opacity-20 hover:bg-opacity-20 transition-all">
              <div className="text-3xl font-bold mb-1">54+</div>
              <div className="text-sm text-blue-100">Tower Sites</div>
            </div>
            <div className="text-center p-4 bg-white bg-opacity-10 backdrop-blur-sm rounded-lg border border-white border-opacity-20 hover:bg-opacity-20 transition-all">
              <div className="text-3xl font-bold mb-1">200+</div>
              <div className="text-sm text-blue-100">FO Segments</div>
            </div>
            <div className="text-center p-4 bg-white bg-opacity-10 backdrop-blur-sm rounded-lg border border-white border-opacity-20 hover:bg-opacity-20 transition-all">
              <div className="text-3xl font-bold mb-1">98%</div>
              <div className="text-sm text-blue-100">Success Rate</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Section - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-gray-50 relative">
        {/* Subtle pattern overlay */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 left-10">
            <Network className="w-32 h-32 text-blue-900" />
          </div>
          <div className="absolute bottom-10 right-10">
            <Building2 className="w-24 h-24 text-blue-900" />
          </div>
        </div>

        <div className="w-full max-w-md relative z-10">
          {/* Logo and Title */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <img 
                src="/logoErabangun.png" 
                alt="EraBangun Logo" 
                className="h-20 w-auto object-contain"
              />
            </div>
            <h1 className="text-3xl font-bold text-gray-900">PMO</h1>
            <p className="text-m text-gray-600 mt-2">Project Management Office</p>
            <p className="text-sm text-gray-500 mt-1">Tower & Fiber Optic Infrastructure</p>
          </div>

          {/* Login Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Selamat Datang</h2>
            <p className="text-gray-600 mb-6">Silakan login untuk melanjutkan</p>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-800">{error}</p>
                </div>
              </div>
            )}

            <form onSubmit={handleLogin} className="space-y-5">
              {/* Username Field */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Username / Email
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Masukkan username atau email"
                    className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>

              {/* Password Field */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Masukkan password"
                    className="w-full pl-11 pr-12 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                    required
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    disabled={isLoading}
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              {/* Remember Me & Forgot Password */}
              <div className="flex items-center justify-between">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                    disabled={isLoading}
                  />
                  <span className="ml-2 text-sm text-gray-700">Ingat saya</span>
                </label>
                <button
                  type="button"
                  onClick={() => toast.info('Hubungi administrator untuk reset password')}
                  className="text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
                  disabled={isLoading}
                >
                  Lupa password?
                </button>
              </div>

              {/* Login Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-red-600 to-red-500 hover:from-red-700 hover:to-red-600 text-white font-semibold py-3 px-4 rounded-lg transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Memproses...</span>
                  </>
                ) : (
                  <span>Login</span>
                )}
              </button>
            </form>
          </div>

          {/* Footer */}
          <div className="text-center mt-6">
            <p className="text-xs text-gray-500">
              © 2025 PT Era Bangun Jaya. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;